import { motion } from "framer-motion";
import { useState } from "react";
import { MapPin, Trophy, Expand } from "lucide-react";
import ChapterHeader from "./ChapterHeader";
import Lightbox from "./Lightbox";
import { CONTENT } from "../data/content";

function TimelineItem({ item, index, total, onOpenCert }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-8%" }}
      transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1] }}
      className="relative pl-8 md:pl-14 pb-14 md:pb-20 border-l border-border last:border-transparent group"
      data-testid={`experience-item-${index}`}
    >
      {/* Node */}
      <div className="absolute -left-[7px] top-1 w-3.5 h-3.5 bg-background border border-accent grid place-items-center">
        <div className="w-1.5 h-1.5 bg-accent" />
      </div>

      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2 mb-4">
        <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-accent">
          {String(total - index).padStart(2, "0")} /
        </span>
        <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
          {item.dates}
        </span>
      </div>

      <h3 className="font-heading text-2xl md:text-4xl font-black tracking-tight uppercase leading-[1] group-hover:text-accent transition-colors">
        {item.company}
      </h3>
      <div className="mt-2 text-sm md:text-base text-foreground/85">
        {item.role}
        {item.team && (
          <span className="text-muted-foreground"> — {item.team}</span>
        )}
      </div>
      {item.location && (
        <div className="mt-1 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <MapPin size={11} /> {item.location}
        </div>
      )}

      <ul className="mt-6 space-y-3 max-w-[68ch]">
        {item.bullets.map((b, i) => (
          <li key={i} className="flex gap-3 text-sm md:text-[15px] leading-relaxed text-foreground/85">
            <span className="mt-2 shrink-0 w-1.5 h-1.5 bg-accent" />
            <span>{b}</span>
          </li>
        ))}
      </ul>

      {item.ops && (
        <div className="mt-6 max-w-[68ch] border-l border-border pl-4 md:pl-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2.5">
            {item.ops.label}
          </div>
          <div className="flex flex-wrap gap-x-2 gap-y-1.5">
            {item.ops.items.map((op, i) => (
              <span
                key={i}
                className="text-xs md:text-[13px] leading-snug text-muted-foreground"
              >
                {op}
                {i < item.ops.items.length - 1 && (
                  <span className="mx-2 text-accent/60">/</span>
                )}
              </span>
            ))}
          </div>
        </div>
      )}
      {item.awards && (
        <div className="mt-8 max-w-[68ch]">
          <div className="flex items-center gap-2.5 mb-4">
            <Trophy size={14} className="text-accent" />
            <span className="font-mono text-[10px] md:text-[11px] uppercase tracking-[0.22em] text-accent">
              Recognition &amp; Awards
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {item.awards.map((award, i) => (
              <figure key={i} data-testid={`award-${index}-${i}`}>
                <button
                  type="button"
                  onClick={() => onOpenCert(award)}
                  aria-label={`Open certificate: ${award.title}`}
                  data-testid={`award-thumb-${index}-${i}`}
                  className="group relative block w-full overflow-hidden border border-border bg-surface hover:border-accent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                  <img
                    src={award.image}
                    alt={award.title}
                    loading="lazy"
                    className="w-full aspect-[1.414/1] object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
                  />
                  <span className="absolute inset-0 bg-background/0 group-hover:bg-background/20 transition-colors" />
                  <span className="absolute bottom-2 right-2 grid place-items-center w-8 h-8 rounded-full bg-background/80 backdrop-blur border border-border text-foreground opacity-0 group-hover:opacity-100 transition-opacity">
                    <Expand size={13} />
                  </span>
                </button>
                <figcaption className="mt-2.5 text-[12px] md:text-[13px] italic leading-snug text-muted-foreground">
                  {award.caption}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
}

export default function Experience() {
  const [cert, setCert] = useState(null);
  return (
    <section
      id="experience"
      data-testid="experience-section"
      className="relative border-t border-border py-24 md:py-40 bg-background"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <ChapterHeader n="04" kicker="Timeline" title={<>Where I&rsquo;ve<br /><span className="text-accent">shipped</span>.</>}>
          <div className="mt-4">
            {CONTENT.experience.map((item, i) => (
              <TimelineItem
                key={item.company}
                item={item}
                index={i}
                total={CONTENT.experience.length}
                onOpenCert={setCert}
              />
            ))}
          </div>
        </ChapterHeader>
      </div>
      <Lightbox
        open={!!cert}
        image={cert?.image}
        title={cert?.title}
        onClose={() => setCert(null)}
      />
    </section>
  );
}
